import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';
import {
  AccountsPayableAction,
  AccountsPayableActionName,
} from '../models/accounts-payable-actions.model';
import { ApNavGroup } from '../models/company-permission.model';
import { ContextService } from './context.service';
import {
  getAccountPayableListTabs,
  sortApListTabsForDesign,
  sortApNavSubMenusForDesign,
} from '../utils/ap-list-tab.util';
import { resolveActiveCompanyId } from '../utils/company-permission.util';

/**
 * UI usage map for BE actionPermissions keys.
 * Documentation + single place to see which feature checks which action.
 */
export const ACCOUNTS_PAYABLE_ACTION_USAGE: Record<
  AccountsPayableActionName,
  readonly string[]
> = {
  [AccountsPayableAction.Create]: [
    'list.createNew',
    'form.saveDraft',
    'form.createRequest',
    'quickAction.createAccountsPayable',
    'quickAction.createBulkAp',
    'bulkApModal.createRequests',
    'route.new',
    'details.saveDraft',
    'details.createRequest',
  ],
  [AccountsPayableAction.View]: [
    'list.viewRequest',
    'route.list',
    'route.view',
    'route.vendorPaymentList',
  ],
  [AccountsPayableAction.DeleteDraft]: [
    'list.deleteDraft',
    'detail.deleteDraft',
  ],
  [AccountsPayableAction.Withdraw]: ['list.withdraw', 'detail.withdraw'],
  [AccountsPayableAction.EditDraftOrReturned]: [
    'list.editRequest.draftOrReturned',
    'detail.editRequest.draftOrReturned',
    'form.updateRequest',
    'route.edit',
  ],
  [AccountsPayableAction.EditApprovedOrPaymentPending]: [
    'list.editRequest.approvedOrPaymentPending',
    'detail.editRequest.approvedOrPaymentPending',
    'form.updateRequest.approved',
    'route.edit.approved',
  ],
  [AccountsPayableAction.Update]: [
    'list.editRequest.pending',
    'detail.editRequest.pending',
    'form.updateRequest.pending',
    'route.edit.pending',
  ],
  [AccountsPayableAction.Duplicate]: ['list.makeCopy', 'detail.makeCopy', 'ai.similarApRecall'],
  [AccountsPayableAction.Return]: ['list.return', 'detail.return'],
  [AccountsPayableAction.Approve]: ['list.approve', 'detail.approve'],
  [AccountsPayableAction.Reject]: ['list.reject', 'detail.reject'],
  [AccountsPayableAction.MakePayment]: [
    'detail.makePayment',
    'payment.tab.makePayment',
  ],
  [AccountsPayableAction.EditPayment]: ['payment.tab.editPayment'],
  [AccountsPayableAction.ReferToCompanyAdmin]: ['detail.referToCompanyAdmin'],
  [AccountsPayableAction.CollatedPayment]: [
    'route.collatedPayment',
    'sidebar.collatedPayment',
  ],
  [AccountsPayableAction.BillEdit]: ['collatedPayment.billEdit'],
  [AccountsPayableAction.BillCreate]: ['collatedPayment.billCreate'],
  [AccountsPayableAction.IntegrateToNetSuite]: ['detail.integrateNetSuite'],
  [AccountsPayableAction.Restore]: ['list.restore', 'detail.restore'],
};

type ApPermissionSnapshot = {
  roleId: string | null;
  companyId: string | null;
  bypassApManager1: boolean;
  actions: ReadonlyMap<string, boolean>;
  navGroups: readonly ApNavGroup[];
  canAccessCollatedPayment: boolean;
};

/**
 * Central facade for AP page/action permissions.
 * Resolve rules stay in ContextService; this service caches lookups and
 * provides one API for UI + guards (behavior-preserving).
 */
@Injectable({
  providedIn: 'root',
})
export class ApPermissionService {
  private readonly snapshot$ = new BehaviorSubject<ApPermissionSnapshot>(
    this.buildSnapshot()
  );

  readonly actions$ = this.snapshot$.pipe(map((s) => s.actions));
  readonly navGroups$ = this.snapshot$.pipe(map((s) => s.navGroups));
  readonly navGroup$ = this.navGroups$.pipe(
    map((groups) => groups[0] ?? null)
  );
  readonly canAccessCollatedPayment$ = this.snapshot$.pipe(
    map((s) => s.canAccessCollatedPayment)
  );

  constructor(private readonly contextSvc: ContextService) {
    combineLatest([
      this.contextSvc.selectedRoleId$.pipe(
        startWith(this.contextSvc.selectedRoleId$.value)
      ),
      this.contextSvc.selectedCompanyId$.pipe(
        startWith(this.contextSvc.getActiveCompanyId())
      ),
      this.contextSvc.user.pipe(startWith(this.contextSvc.user.value)),
      this.contextSvc.bypassApManager1$,
      this.contextSvc.onApRolePermissionsChanged$.pipe(startWith(null)),
      this.contextSvc.onRoleSwitch$.pipe(startWith(null)),
    ])
      .pipe(
        map(() => this.buildSnapshot()),
        distinctUntilChanged(
          (a, b) =>
            a.roleId === b.roleId &&
            a.companyId === b.companyId &&
            a.bypassApManager1 === b.bypassApManager1 &&
            a.canAccessCollatedPayment === b.canAccessCollatedPayment &&
            this.sameActionMap(a.actions, b.actions) &&
            JSON.stringify(a.navGroups) === JSON.stringify(b.navGroups)
        )
      )
      .subscribe((snapshot) => this.snapshot$.next(snapshot));
  }

  /** Same rule as ContextService.isAccountsPayableActionAllowed. */
  can(action: AccountsPayableActionName | string): boolean {
    const snapshot = this.snapshot$.value;
    if (snapshot.actions.has(action)) {
      return snapshot.actions.get(action) === true;
    }

    return this.contextSvc.isAccountsPayableActionAllowed(action);
  }

  canAny(actions: ReadonlyArray<AccountsPayableActionName | string>): boolean {
    return actions.some((action) => this.can(action));
  }

  canAll(actions: ReadonlyArray<AccountsPayableActionName | string>): boolean {
    return actions.every((action) => this.can(action));
  }

  getNavGroups(): ApNavGroup[] {
    return [...this.snapshot$.value.navGroups];
  }

  getNavGroup(): ApNavGroup | null {
    return this.getNavGroups()[0] ?? null;
  }

  hasSidebar(): boolean {
    return this.getNavGroups().some((group) => group.subMenus?.length > 0);
  }

  getAllSubMenus() {
    return this.contextSvc.getAccountsPayableSubMenus();
  }

  getAccountPayableListTabs() {
    return sortApListTabsForDesign(
      getAccountPayableListTabs(
        this.contextSvc.getStoredPermissionsForRole(
          this.snapshot$.value.roleId
        ),
        this.snapshot$.value.companyId
      )
    );
  }

  canAccessCollatedPayment(): boolean {
    return this.snapshot$.value.canAccessCollatedPayment;
  }

  canCreate(): boolean {
    return this.can(AccountsPayableAction.Create);
  }

  canView(): boolean {
    return this.can(AccountsPayableAction.View) || this.hasSidebar();
  }

  canEditRoute(): boolean {
    return this.canAny([
      AccountsPayableAction.EditDraftOrReturned,
      AccountsPayableAction.Update,
      AccountsPayableAction.EditApprovedOrPaymentPending,
    ]);
  }

  refresh(): void {
    this.snapshot$.next(this.buildSnapshot());
  }

  private buildSnapshot(): ApPermissionSnapshot {
    const roleId =
      this.contextSvc.selectedRoleId$.value ??
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('selectedRoleId')
        : null);

    const actions = new Map<string, boolean>();
    (Object.values(AccountsPayableAction) as string[]).forEach((action) => {
      actions.set(action, this.contextSvc.isAccountsPayableActionAllowed(action));
    });

    return {
      roleId,
      companyId: resolveActiveCompanyId(
        this.contextSvc.user.value?.userRoles,
        this.contextSvc.getCompanyId()
      ),
      bypassApManager1: this.contextSvc.isBypassApManager1Enabled(),
      actions,
      navGroups: this.contextSvc
        .getAccountsPayableNavGroups()
        .map((group) => ({
          ...group,
          subMenus: sortApNavSubMenusForDesign(group.subMenus ?? []),
        })),
      canAccessCollatedPayment: this.contextSvc.canAccessCollatedPayment(),
    };
  }

  private sameActionMap(
    a: ReadonlyMap<string, boolean>,
    b: ReadonlyMap<string, boolean>
  ): boolean {
    if (a.size !== b.size) {
      return false;
    }
    for (const [key, value] of a) {
      if (b.get(key) !== value) {
        return false;
      }
    }
    return true;
  }
}
